/**
 * RichTextEditor
 *
 * Markdown editor with a toolbar for inserting CipherOps constructs: bold,
 * italic, inline code, fenced code blocks (language picker), images (file →
 * base64), and local screen-recording video embeds.
 *
 * Value is always plain Markdown text. The right-pane live preview in the
 * Findings master-detail view is the source of truth for rendered output —
 * we deliberately do NOT render a second preview here, since two markdown
 * pipelines (this one + packages/renderer) would drift.
 *
 * The optional ⛶ button at the right edge of the toolbar is shown only when
 * the parent passes onToggleExpand. The parent decides what "expand" means
 * (e.g. grow the field to fill its container).
 */

import React, { useEffect, useRef, useState } from 'react';
import { invoke } from '../lib/ipc';
import { handleMediaPaste, type PasteUploader } from '../lib/mediaPaste';

// History cap — keeping ~50 entries gives Word-style "undo a lot of stuff"
// without unbounded memory use. Each entry is the full markdown string;
// 50 × a few KB is negligible.
const HISTORY_CAP = 50;
// Typing debounce — chars typed within this window collapse into a single
// undo entry, so Ctrl+Z reverses words rather than individual characters.
const TYPING_DEBOUNCE_MS = 500;

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Approximate visible rows in normal (non-expanded) mode (default 8) */
  rows?: number;
  /** When true, the textarea fills its parent vertically via flex. */
  isExpanded?: boolean;
  /** When provided, shows the ⛶/✗ button. The parent owns the expand state. */
  onToggleExpand?: () => void;
  /** Fired when the textarea receives focus. Used by master-detail / Report
   *  Builder to scroll the live-preview iframe to the matching section. */
  onFocus?: () => void;
  /** Optional upload-and-reference paste strategy. When supplied, pasting
   *  an image / video routes the blob through this callback (typically
   *  attaches as evidence on the parent finding) and inserts a SHORT
   *  markdown reference like `![caption](evidence:caption)` instead of
   *  inlining a multi-MB base64 data URI into the textarea. The renderer
   *  resolves the reference at render time via rewriteEvidenceRefs.
   *  Without this prop the paste handler falls back to the legacy
   *  data-URI inline mode. */
  pasteUploader?: PasteUploader;
  /** 2026-05-22 — fires on focus with the textarea DOM element AND a
   *  setValue callback bound to the current onChange. The Findings +
   *  Report Builder screens use this to register the "last focused
   *  narrative" with the Engagement Media Library so click-to-insert
   *  knows where to splice. Optional — surfaces don't break when omitted. */
  onFocusWithRef?: (args: { el: HTMLTextAreaElement; getValue: () => string; setValue: (next: string) => void }) => void;
  /** 2026-05-22 — when true, hides the "Add image / video" toolbar button
   *  AND the divider before it. Used by EditLibraryModal: library entries
   *  are templates and have no engagement context, so media inserts make
   *  no sense — that work belongs to the finding / report-builder stage
   *  where media is bound to a specific engagement. */
  disableMediaInsert?: boolean;
  /** a11y 2026-06-30 — accessible name for the editor textarea. The visible
   *  label sits outside this component (a `.form-label` in the parent), so the
   *  control itself has no programmatic name; pass the field's label here so a
   *  screen reader announces it (e.g. "Description, edit text"). Falls back to
   *  a generic "Rich text editor" when omitted. */
  ariaLabel?: string;
}

export default function RichTextEditor({ value, onChange, placeholder, rows = 8, isExpanded = false, onToggleExpand, onFocus, pasteUploader, onFocusWithRef, disableMediaInsert = false, ariaLabel }: Props) {
  const taRef = useRef<HTMLTextAreaElement>(null);

  // ── Custom undo / redo stack ─────────────────────────────────────────────
  // Word-style undo that captures EVERY value change, regardless of whether
  // it came from typing, paste, a toolbar button (B/I/S, lists, alignment,
  // evidence block), or a media insert (image/video). The previous
  // execCommand-based approach only caught native textarea events, leaving
  // toolbar operations un-undoable.
  //
  // Two flavours of snapshot:
  //   - commit(next)        — toolbar/media ops; immediate snapshot
  //   - commitTyping(next)  — textarea onChange; debounced snapshot so
  //                            consecutive keystrokes collapse into one entry
  //
  // applyingHistoryRef guards against re-entrant snapshots while we're
  // applying an undo/redo (otherwise pop+onChange would push a new entry).
  const undoRef           = useRef<string[]>([]);
  const redoRef           = useRef<string[]>([]);
  const lastSnapshotRef   = useRef<string>(value);
  const applyingHistoryRef = useRef(false);
  const typingTimerRef    = useRef<ReturnType<typeof setTimeout> | null>(null);

  // State exists so the buttons can render disabled when the relevant stack
  // is empty. Refs alone wouldn't trigger a re-render.
  const [undoCount, setUndoCount] = useState(0);
  const [redoCount, setRedoCount] = useState(0);

  // Reset history when the parent swaps the entire value to something
  // unrelated (e.g. user clicks a different finding). Detected by a
  // mismatch between the incoming value and our last-known snapshot
  // when no operation we initiated would have produced it.
  useEffect(() => {
    if (applyingHistoryRef.current) return;          // we're mid-undo/redo
    if (value === lastSnapshotRef.current) return;   // we already accounted for it
    // Outside change — reset history rather than letting it diverge.
    undoRef.current = [];
    redoRef.current = [];
    lastSnapshotRef.current = value;
    setUndoCount(0);
    setRedoCount(0);
  }, [value]);

  const pushSnapshot = (oldValue: string) => {
    if (oldValue === lastSnapshotRef.current) return;
    undoRef.current.push(lastSnapshotRef.current);
    if (undoRef.current.length > HISTORY_CAP) undoRef.current.shift();
    redoRef.current = [];
    lastSnapshotRef.current = oldValue;
    setUndoCount(undoRef.current.length);
    setRedoCount(0);
  };

  // Toolbar / media ops route here — immediate snapshot.
  const commit = (next: string) => {
    pushSnapshot(next);
    onChange(next);
  };

  // Textarea onChange routes here — debounced snapshot. The actual onChange
  // is called immediately so the cursor / selection feel responsive; the
  // history snapshot lands TYPING_DEBOUNCE_MS after the user stops typing.
  const commitTyping = (next: string) => {
    onChange(next);
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      if (next !== lastSnapshotRef.current) {
        undoRef.current.push(lastSnapshotRef.current);
        if (undoRef.current.length > HISTORY_CAP) undoRef.current.shift();
        redoRef.current = [];
        lastSnapshotRef.current = next;
        setUndoCount(undoRef.current.length);
        setRedoCount(0);
      }
    }, TYPING_DEBOUNCE_MS);
  };

  const handleUndo = () => {
    // Flush any pending typing snapshot first so a fast Ctrl+Z right after
    // typing reverses what was just typed instead of nothing.
    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = null;
      if (value !== lastSnapshotRef.current) {
        undoRef.current.push(lastSnapshotRef.current);
        if (undoRef.current.length > HISTORY_CAP) undoRef.current.shift();
        lastSnapshotRef.current = value;
      }
    }
    if (undoRef.current.length === 0) return;
    const previous = undoRef.current.pop()!;
    redoRef.current.push(value);
    if (redoRef.current.length > HISTORY_CAP) redoRef.current.shift();
    lastSnapshotRef.current = previous;
    applyingHistoryRef.current = true;
    onChange(previous);
    // Clear the guard on the next tick so React's effects see the new value
    // before the guard resets.
    queueMicrotask(() => { applyingHistoryRef.current = false; });
    setUndoCount(undoRef.current.length);
    setRedoCount(redoRef.current.length);
    requestAnimationFrame(() => taRef.current?.focus());
  };

  const handleRedo = () => {
    if (redoRef.current.length === 0) return;
    const next = redoRef.current.pop()!;
    undoRef.current.push(value);
    if (undoRef.current.length > HISTORY_CAP) undoRef.current.shift();
    lastSnapshotRef.current = next;
    applyingHistoryRef.current = true;
    onChange(next);
    queueMicrotask(() => { applyingHistoryRef.current = false; });
    setUndoCount(undoRef.current.length);
    setRedoCount(redoRef.current.length);
    requestAnimationFrame(() => taRef.current?.focus());
  };

  // Capture Ctrl+Z / Ctrl+Y / Ctrl+Shift+Z so the textarea's native undo
  // doesn't fight ours. preventDefault stops the browser from also reverting.
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isMod = e.ctrlKey || e.metaKey;
    if (!isMod) return;
    if (e.key === 'z' && !e.shiftKey) { e.preventDefault(); handleUndo(); return; }
    if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) { e.preventDefault(); handleRedo(); return; }
  };

  // ── Wrap selected text (or placeholder) with before/after strings ────────
  const wrap = (before: string, after: string, ph = '') => {
    const ta = taRef.current;
    if (!ta) return;
    const s   = ta.selectionStart;
    const e   = ta.selectionEnd;
    const sel = value.slice(s, e) || ph;
    const next = value.slice(0, s) + before + sel + after + value.slice(e);
    commit(next);
    requestAnimationFrame(() => {
      ta.focus();
      const cur = s + before.length + sel.length;
      ta.setSelectionRange(cur, cur);
    });
  };

  // ── Toggle a line-level prefix on all selected lines (lists, blockquote) ─
  const toggleLinePrefix = (marker: string) => {
    const ta = taRef.current;
    if (!ta) return;
    const s = ta.selectionStart;
    const e = ta.selectionEnd;
    const beforeSel = value.slice(0, s);
    const lineStart = beforeSel.lastIndexOf('\n') + 1;
    const afterIdx  = value.indexOf('\n', e);
    const lineEnd   = afterIdx === -1 ? value.length : afterIdx;
    const lines     = value.slice(lineStart, lineEnd).split('\n');
    const allHave   = lines.every(l => l.startsWith(marker));
    const newLines  = allHave
      ? lines.map(l => l.startsWith(marker) ? l.slice(marker.length) : l)
      : lines.map(l => l.startsWith(marker) ? l : marker + l);
    commit(value.slice(0, lineStart) + newLines.join('\n') + value.slice(lineEnd));
    requestAnimationFrame(() => ta.focus());
  };

  // ── Wrap selection in an alignment block :::align … ::: ──────────────────
  const alignBlock = (align: 'left' | 'center' | 'right' | 'justify') => {
    const ta = taRef.current;
    if (!ta) return;
    const s   = ta.selectionStart;
    const e   = ta.selectionEnd;
    const sel = value.slice(s, e).trim() || 'Text here';
    const sep = value.slice(0, s).endsWith('\n') || s === 0 ? '' : '\n';
    const block = `${sep}:::${align}\n${sel}\n:::\n`;
    commit(value.slice(0, s) + block + value.slice(e));
    requestAnimationFrame(() => ta.focus());
  };

  // ── Insert a fenced evidence block ───────────────────────────────────────
  // The renderer treats every fenced block the same (no per-language syntax
  // highlighting), so we use a single "Evidence" button rather than a
  // language picker. Pentesters are pasting evidence — terminal output, HTTP
  // captures, scanner snippets, command output — so that's the framing.
  const insertEvidenceBlock = () => {
    const ta = taRef.current;
    if (!ta) return;
    const s = ta.selectionStart;
    const e = ta.selectionEnd;
    const sel = value.slice(s, e) || 'paste evidence here';
    const block = `\`\`\`\n${sel}\n\`\`\``;
    commit(value.slice(0, s) + block + value.slice(e));
    requestAnimationFrame(() => ta.focus());
  };

  // ── Read a File as base64 (no data:URI prefix) ──────────────────────────
  // FileReader works in the sandboxed renderer; File.path does not. We send
  // bytes to the main process via cipherops/media/save which writes them to
  // userData/cipherops-media/ and returns the resulting absolute path.
  const readFileAsBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(',');
      resolve(comma === -1 ? result : result.slice(comma + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

  // Extract the basename from an absolute path (last segment after / or \).
  // Used to build the compact `media:basename.ext` token — the renderer's
  // service-layer resolver looks the file up in userData/cipherops-media/.
  const toMediaToken = (absPath: string) => {
    const parts = absPath.split(/[\\/]/);
    return parts[parts.length - 1];
  };

  // ── Image: copy to userData/cipherops-media/ via IPC, insert compact ref ─
  // The full base64 used to live inside the markdown which made the textarea
  // unreadable for any non-trivial image. Now markdown holds only a
  // file:/// reference (~120 chars); the renderer expands it back to a data
  // URI at render time.
  const insertImage = () => {
    const input = document.createElement('input');
    input.type   = 'file';
    input.accept = 'image/*';
    input.onchange = async (ev) => {
      const file = (ev.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const content_b64 = await readFileAsBase64(file);
        const result = await invoke<{ media_path: string }>('cipherops/media/save', {
          filename: file.name,
          content_b64,
        });
        const token = toMediaToken(result.media_path);
        const sep = value && !value.endsWith('\n') ? '\n' : '';
        commit(value + sep + `![${file.name}](media:${token})\n`);
      } catch (err) {
        // Surface failure inline so the consultant notices, but never block.
        const sep = value && !value.endsWith('\n') ? '\n' : '';
        const msg = err instanceof Error ? err.message : 'unknown error';
        commit(value + sep + `_[image upload failed: ${msg}]_\n`);
      }
    };
    input.click();
  };

  // ── Video: copy to userData/cipherops-media/ via IPC, insert compact ref ─
  const insertVideo = () => {
    const input = document.createElement('input');
    input.type   = 'file';
    input.accept = 'video/mp4,video/webm,video/ogg,video/quicktime,video/x-msvideo,video/*';
    input.onchange = async (ev) => {
      const file = (ev.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const content_b64 = await readFileAsBase64(file);
        const result = await invoke<{ media_path: string }>('cipherops/media/save', {
          filename: file.name,
          content_b64,
        });
        const token = toMediaToken(result.media_path);
        const sep = value && !value.endsWith('\n') ? '\n' : '';
        commit(value + sep + `[video:media:${token}]\n`);
      } catch (err) {
        const sep = value && !value.endsWith('\n') ? '\n' : '';
        const msg = err instanceof Error ? err.message : 'unknown error';
        commit(value + sep + `_[video upload failed: ${msg}]_\n`);
      }
    };
    input.click();
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const btn = (active = false): React.CSSProperties => ({
    background: active ? 'var(--border)' : 'transparent',
    border: '1px solid var(--border)',
    borderRadius: 3,
    padding: '2px 8px',
    cursor: 'pointer',
    fontFamily: 'var(--mono)',
    fontSize: 11,
    color: 'var(--text)',
    lineHeight: '18px',
    whiteSpace: 'nowrap',
  });

  const divider: React.CSSProperties = {
    width: 1, height: 18, background: 'var(--border)', margin: '0 4px', flexShrink: 0,
  };

  const minH = rows * 24;

  return (
    <div style={isExpanded
      ? { display: 'flex', flexDirection: 'column', gap: 0, flex: 1, minHeight: 0, height: '100%' }
      : { display: 'flex', flexDirection: 'column', gap: 0 }
    }>
      {/* ── Toolbar ─────────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap',
        padding: '4px 8px',
        background: 'var(--surface-alt)',
        border: '1px solid var(--border)',
        borderBottom: 'none',
        borderRadius: '4px 4px 0 0',
      }}>

        {/* Undo / Redo — custom history captures every operation (typing,
            paste, toolbar buttons, media inserts) so Ctrl+Z reverses anything
            you just did, Word-style. Buttons disable when stacks are empty. */}
        <button type="button"
          style={{ ...btn(), opacity: undoCount === 0 ? 0.4 : 1, cursor: undoCount === 0 ? 'default' : 'pointer' }}
          title={`Undo (Ctrl+Z) — ${undoCount} step${undoCount === 1 ? '' : 's'} available`}
          aria-label="Undo"
          disabled={undoCount === 0}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleUndo}>↶</button>
        <button type="button"
          style={{ ...btn(), opacity: redoCount === 0 ? 0.4 : 1, cursor: redoCount === 0 ? 'default' : 'pointer' }}
          title={`Redo (Ctrl+Y) — ${redoCount} step${redoCount === 1 ? '' : 's'} available`}
          aria-label="Redo"
          disabled={redoCount === 0}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleRedo}>↷</button>

        <div style={divider} />

        {/* Inline formatting */}
        <button type="button" style={{ ...btn(), fontWeight: 700 }}
          aria-label="Bold"
          title="Bold" onClick={() => wrap('**', '**', 'bold text')}>B</button>
        <button type="button" style={{ ...btn(), fontStyle: 'italic' }}
          aria-label="Italic"
          title="Italic" onClick={() => wrap('*', '*', 'italic text')}>I</button>
        <button type="button" style={{ ...btn(), textDecoration: 'line-through' }}
          aria-label="Strikethrough"
          title="Strikethrough" onClick={() => wrap('~~', '~~', 'text')}>S</button>
        <button type="button" style={btn()}
          aria-label="Inline code"
          title="Inline code" onClick={() => wrap('`', '`', 'code')}>{`\` \``}</button>

        <div style={divider} />

        {/* Lists */}
        <button type="button" style={btn()}
          aria-label="Bullet list"
          title="Bullet list — toggles • prefix on selected lines"
          onClick={() => toggleLinePrefix('- ')}>• ─</button>
        <button type="button" style={btn()}
          aria-label="Numbered list"
          title="Numbered list — toggles 1. prefix on selected lines"
          onClick={() => toggleLinePrefix('1. ')}>1. ─</button>
        <button type="button" style={btn()}
          aria-label="Blockquote"
          title="Blockquote — toggles > prefix on selected lines"
          onClick={() => toggleLinePrefix('> ')}>" ─</button>

        <div style={divider} />

        {/* Alignment */}
        <button type="button" style={btn()}
          aria-label="Align left"
          title="Left align" onClick={() => alignBlock('left')}>≡L</button>
        <button type="button" style={btn()}
          aria-label="Align centre"
          title="Centre align" onClick={() => alignBlock('center')}>≡C</button>
        <button type="button" style={btn()}
          aria-label="Align right"
          title="Right align" onClick={() => alignBlock('right')}>≡R</button>
        <button type="button" style={btn()}
          aria-label="Justify"
          title="Justify" onClick={() => alignBlock('justify')}>≡J</button>

        <div style={divider} />

        {/* Evidence block — wraps the selection (or a placeholder) in
            triple-backtick fences. Renders as a monospace block in the live
            preview. */}
        <button type="button" style={btn()}
          title="Wrap selection in an evidence block (terminal output, HTTP capture, command output, scanner snippet)"
          onClick={insertEvidenceBlock}>
          {'{ }'} Evidence
        </button>

        {/* 2026-05-22 — Image / Video entry point. Hidden when the editor
            is mounted in a context with no engagement scope (e.g. the
            Finding Library template editor) — there's no engagement to
            attach media to and no Media Library tab to open. */}
        {!disableMediaInsert && (
          <>
            <div style={divider} />
            <button
              type="button"
              style={btn()}
              title="Opens the engagement Media Library on the left. Click a thumbnail to drop it at the cursor here, or drag a thumbnail straight onto the line where you want it."
              onClick={() => {
                window.dispatchEvent(new CustomEvent('cipherops:open-media-tab'));
              }}
            >
              Add image / video &rarr;
            </button>
          </>
        )}

        {/* Expand / collapse — only shown when parent opted in */}
        {onToggleExpand && (
          <div style={{ marginLeft: 'auto' }}>
            <button
              type="button"
              style={{
                ...btn(isExpanded),
                fontSize: 14,
                lineHeight: '14px',
                padding: '2px 9px',
                ...(isExpanded ? { background: 'var(--accent, #8b1a2e)', color: '#fff', borderColor: 'var(--accent, #8b1a2e)' } : {}),
              }}
              title={isExpanded ? 'Collapse field' : 'Expand to fill the editor pane'}
              aria-label={isExpanded ? 'Collapse field' : 'Expand field'}
              aria-pressed={isExpanded}
              onClick={onToggleExpand}
            >
              {isExpanded ? '✗' : '⛶'}
            </button>
          </div>
        )}
      </div>

      {/* ── Editor — single textarea, no preview tab ─────────────────── */}
      <textarea
        ref={taRef}
        className="form-textarea"
        aria-label={ariaLabel ?? 'Rich text editor'}
        style={isExpanded ? {
          borderRadius: '0 0 4px 4px',
          flex: 1,
          minHeight: 0,
          resize: 'none',
          fontFamily: 'var(--mono)',
          fontSize: 12,
          lineHeight: 1.6,
        } : {
          borderRadius: '0 0 4px 4px',
          minHeight: minH,
          resize: 'vertical',
          fontFamily: 'var(--mono)',
          fontSize: 12,
          lineHeight: 1.6,
        }}
        value={value}
        onChange={e => commitTyping(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={(e) => {
          onFocus?.();
          // 2026-05-22 — register with Media Library focus tracker.
          // Captures the current onChange via a closure so a later
          // splice (from the Media tab's onInsert) reaches the same
          // setter that handles typing, keeping the editor's undo
          // stack + auto-save flow consistent.
          if (onFocusWithRef) {
            const el = e.currentTarget;
            onFocusWithRef({
              el,
              getValue: () => value,
              setValue: (next: string) => commit(next),
            });
          }
        }}
        /* 2026-05-21 — paste an image or video from the clipboard and it
           gets converted to an inline markdown embed (data URI for images,
           [video:data:...] for videos). Anything that isn't a media file
           falls through to the default paste behaviour so text paste still
           works. Routed through `commit` (not commitTyping) because a
           media paste is a discrete edit, not part of a typing burst —
           we want Ctrl+Z to roll back the entire embed in one step. */
        onPaste={(e) => {
          void handleMediaPaste(e, value, (next) => commit(next), { uploader: pasteUploader });
        }}
        placeholder={placeholder ?? 'Write in Markdown…\n\nUse the toolbar above to insert images, code blocks, and screen recordings. Or just paste a screenshot / video from your clipboard (Ctrl+V) and it embeds inline.'}
      />

    </div>
  );
}
